import './tabupload.css';
import "../../../node_modules/bootstrap-icons/font/bootstrap-icons.css";
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Modal, Button, Form } from 'react-bootstrap';
import TabMap from '../TabUpload/TabMap';
import { useTranslation } from 'react-i18next';
import { useToast } from '../ToastContext/ToastContext';
import { ServiceFactory } from '../../Api/Services/ServiceFactory';
import { ServiceNames } from '../../Api/registry/Services';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPerson } from '../../store/slices/personSlice';

export default function TabUpload() {
  const { t } = useTranslation(['usersetting', 'common', 'error', 'tabupload']);
  const { showToast } = useToast();
  const [userService, setUserService] = useState(null);
  const personData = useSelector((state) => state.person?.person);
  const loading = useSelector((state) => state.person?.loading);
  const dispatch = useDispatch();
  const [parcels, setParcels] = useState(null);
  const [serviceLoading, setServiceLoading] = useState(true);
  const [showModalSave, setShowModalSave] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ parcelId: null, name: '', description: '' });
  const [pendingGeojson, setPendingGeojson] = useState(null);
  const isFeatureCollection = (g) => g && g.type === 'FeatureCollection' && Array.isArray(g.features);
  const isFeature = (g) => g && g.type === 'Feature' && g.geometry;

  const parseMaybe = (v) => {
    if (!v) return null;
    if (typeof v === 'string') {
      try { return JSON.parse(v); } catch { return null; }
    }
    return v;
  };

  // La usiamo per parsare i campi che vengono dal db. 
  // Siamo sicuri che la geometry è valida perchè abbiamo calcolato centroide e geometry prima di memorizzare la parcel
  // Quindi usiamo la stessa logica di quando l'abbiamo memorizzata:
  // Se collection usiamo la prima 
  // Se geometry multipoligono usiamo il primo poligono
  const pickGeometryFromDB = (geojsonish) => {
    const g = parseMaybe(geojsonish);
    if (!g) return null;
    if (isFeatureCollection(g)) {
      const f = g.features?.[0];
      return f?.geometry || null;
    }
    if (isFeature(g)) return g.geometry;
    return g;
  };

  // Leaflet vuole [lat,lng], il GeoJSON è [lng,lat]
  const lonLatToLatLng = ([lng, lat]) => [lat, lng];

  // Estrae il ring esterno del poligono come [ [lat,lng], ... ]
  // - Polygon: primo ring
  // - MultiPolygon: PRIMO poligono, ring esterno

  const extractOuterRingLatLngFromDBGeojson = (geojsonish) => {
    const geom = pickGeometryFromDB(geojsonish);
    if (!geom || !geom.type || !geom.coordinates) return null;

    if (geom.type === 'Polygon') {
      const outer = geom.coordinates?.[0];
      return Array.isArray(outer) ? outer.map(lonLatToLatLng) : null;
    }

    if (geom.type === 'MultiPolygon') {
      const firstOuter = geom.coordinates?.[0]?.[0];
      return Array.isArray(firstOuter) ? firstOuter.map(lonLatToLatLng) : null;
    }

    // altri tipi non sarebbero parcel valide (Point/LineString/…) non sono usabili come outring non li disegnamo sulla mappa
    return null;
  };

  const extractCentroidFromParcelDB = (parcel) => {
    const { latitude, longitude } = parcel || {};
    if (typeof latitude === 'number' && typeof longitude === 'number') {
      return [latitude, longitude];
    }
    return [0, 0]; // fallback di sicurezza
  };

  const extractCoordsFromParcelDB = (parcel) => {
    const fromGeojson = extractOuterRingLatLngFromDBGeojson(parcel?.geojson);
    if (fromGeojson && fromGeojson.length) return fromGeojson;

    const fromGeometry = extractOuterRingLatLngFromDBGeojson(parcel?.geometry);
    if (fromGeometry && fromGeometry.length) return fromGeometry;

    return null;
  };

  const buildParcelFromPersonData = (parcel) => {
    const coords = extractCoordsFromParcelDB(parcel);
    const centroid = extractCentroidFromParcelDB(parcel);

    return {
      parcelId: parcel.id,
      name: parcel.name,
      description: parcel.description,
      area: parcel.area,
      coords: coords || [],
      centroid,
      soilData: {
        sandPercentage: parcel.sandPercentage,
        siltPercentage: parcel.siltPercentage,
        clayPercentage: parcel.clayPercentage,
        soilType: parcel.soilType,
        ph: parcel.ph,
        organicMatter: parcel.organicMatter,
        texture: parcel.texture
      },
      monitoring: {}
    };
  };

  const saveParcel = ({ parcelId, name, description, geojson }) => {
    // pre-compila i campi se arrivano dal map component
    setFormData({
      parcelId: parcelId ?? null,
      name: name ?? '',
      description: description ?? ''
    });
    setPendingGeojson(geojson ?? null);
    setShowModalSave(true);
  };

  const handleCloseSave = () => {
    if (saving) return; // evita chiusure durante il salvataggio
    setShowModalSave(false);
    setPendingGeojson(null);
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    if (id === 'form.ControlLayerName') {
      setFormData(prev => ({ ...prev, name: value }));
    } else if (id === 'form.ControlLayerDescription') {
      setFormData(prev => ({ ...prev, description: value }));
    }
  };

  const handleConfirmSave = async () => {
    if (!userService) return;
    setSaving(true);
    try {
      const request = {
        parcelId: formData.parcelId ?? undefined,
        name: formData.name?.trim() || undefined,
        description: formData.description?.trim() || undefined,
        geojson: pendingGeojson // deve essere un GeoJSON valido (EPSG:4326 [lng,lat])
      };
      await userService.createOrUpdateParcelFromGeoJson(request);
      showToast(t("common:messages.success"), "success");
      setShowModalSave(false);
      setPendingGeojson(null);
      // refresh dati persona/parcelle dopo salvataggio
      dispatch(fetchPerson());
    } catch (err) {
      showToast(err.message || t("error:wrong"), "error");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!personData?.parcels) return;

    const parcelsForMap = personData.parcels
      .map(buildParcelFromPersonData)
      .filter(p => Array.isArray(p.coords) && p.coords.length >= 3);

    setParcels(parcelsForMap);
  }, [personData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const service = await ServiceFactory.getService(ServiceNames.UserService);
      if (!cancelled) {
        setUserService(service);
        setServiceLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isLoading = loading || serviceLoading || parcels === null;
  const isUpdate = !!formData?.parcelId;
  const modalTitle = isUpdate
    ? `Update Parcel${formData?.name ? `: ${formData.name}` : ''}`
    : t("tabupload:messages.createNewParcel");

  return (
    <Container fluid style={{ padding: 0, height: '100%', position: 'relative' }}>
      {/* Overlay loading */}
      {isLoading && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: 'rgba(255,255,255,0.6)', zIndex: 10
        }}>
          <div className="d-flex flex-column align-items-center">
            <Spinner animation="border" role="status" />
            <div className="mt-2 text-muted" style={{ fontSize: 14 }}>
              {t("tabupload:messages.uploadDataParcel")}
            </div>
          </div>
        </div>
      )}

      <Row style={{ height: '100%', margin: 0 }}>
        <Col className="mapstore-col" style={{ overflow: 'hidden', position: 'relative' }}>
          {!isLoading && (
            <TabMap parcels={parcels || []} onSaveLayer={saveParcel} />
          )}
        </Col>
      </Row>
      {/* ---- MODAL di salvataggio spostata dalla mappa a qui nel contenitore ---- */}
      <Modal key={formData.parcelId || 'create'}
        show={showModalSave} onHide={handleCloseSave} centered>

        <Modal.Header closeButton={!saving}>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="layer-name" controlId="form.ControlLayerName">
              <Form.Label>{t("tabupload:labels.parcelName")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t("tabupload:labels.parcelName")}
                value={formData.name || ''}
                readOnly={!!formData?.parcelId}      // readonly in Update
                onChange={handleChange}
                disabled={saving}
              />
            </Form.Group>
            <Form.Group className="layer-description mt-3" controlId="form.ControlLayerDescription">
              <Form.Label>{t("tabupload:labels.parcelDescription")}</Form.Label>
              <Form.Control
                as="textarea"
                placeholder={t("tabupload:labels.description")}
                rows={3}
                value={formData.description || ''}
                readOnly={!!formData?.parcelId}      // readonly in Update
                onChange={handleChange}
                disabled={saving}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="close-save-modal-layer" onClick={handleCloseSave} disabled={saving}>
            {t("tabupload:buttons.cancel")}
          </Button>
          <Button
            className="save-layer"
            onClick={handleConfirmSave}
            disabled={saving || !pendingGeojson}
          >
            {saving ? (<><Spinner size="sm" className="me-2" /> {t("tabupload:messages.saving")} </>) : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};